'use client'

import { useMemo, useRef, useState } from 'react'

import { ContextualProductCarousel } from '@/app/components/modules/analytics/ContextualProductCarousel'
import { toModuleAnalyticsItems } from '@/app/components/modules/analytics/buildModuleListAnalytics'
import {
  WEEKEND_LIST_NAME,
  WEEKEND_MODULE_NAME,
  WEEKEND_MODULE_TYPE,
} from '@/app/components/modules/analytics/constants'
import { LoadingOverlay } from '@/app/components/shared/LoadingOverlay'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { NOT_SET } from '@/app/lib/analytics/constants'
import { ALL_LOCATIONS } from '@/app/lib/constants'
import { toProductCards } from '@/app/lib/api/mapProductToCard'
import type { ProductApiItem } from '@/app/lib/api/types-api'

import { fetchFinDeSemanaAction } from './actions'
import { DATE_RANGE_CHANGE_LOADING_LABEL, DEFAULT_DATE_RANGE, EMPTY_STATE_TITLE } from './FinDeSemana.constants'
import { FinDeSemanaHeader } from './FinDeSemanaHeader'
import styles from './FinDeSemana.module.scss'
import type { DateRangeValue } from './FinDeSemana.types'

interface FinDeSemanaClientProps {
  readonly moduleId: string | number
  readonly headingId: string
  readonly pageSlug: string
  /** Provincia de la página anfitriona (URL) — fija durante la vida de esta
   * instancia: cambiarla navega a otra página (ver `FinDeSemanaHeader`), no
   * actualiza este componente en el sitio. */
  readonly province: string
  readonly initialContents: ProductApiItem[]
  /** `module_position` de la analítica (`position` del CMS, o `not_set`). */
  readonly modulePosition: string
}

/**
 * Subárbol interactivo del módulo `FinDeSemana`: cabecera (`FinDeSemanaHeader`
 * — provincia + chips de periodo) + carrusel de tarjetas (`ProductCarousel`,
 * el mismo ya usado por el módulo "Productos").
 *
 * Solo la chip de periodo dispara `fetchFinDeSemanaAction` en segundo plano
 * desde aquí (contracts/fin-de-semana-server-action.md) — nunca navega ni
 * recarga la página (spec.md FR-009). El cambio de provincia lo resuelve
 * `FinDeSemanaHeader` por su cuenta, navegando (mismo comportamiento que
 * "ProductosLocalizados"), así que esta instancia se remonta entera con la
 * nueva `province` — no necesita gestionar ese eje.
 *
 * Mientras la petición de chip está en curso se muestra el `LoadingOverlay`
 * general del sitio (corrección de negocio: mismo indicador que usa el
 * cambio de provincia en `FinDeSemanaHeader`, no un spinner local propio).
 *
 * `latestRequestedKeyRef` descarta cualquier respuesta que ya no corresponda
 * a la chip vigente (el visitante cambió de chip varias veces antes de que la
 * primera petición resolviera) — mismo patrón que `AgendaClient`.
 */
export function FinDeSemanaClient({
  moduleId,
  headingId,
  pageSlug,
  province,
  initialContents,
  modulePosition,
}: FinDeSemanaClientProps) {
  const pageAnalytics = useOptionalPageAnalytics()
  const [dateRange, setDateRange] = useState<DateRangeValue>(DEFAULT_DATE_RANGE)
  const [contents, setContents] = useState<ProductApiItem[]>(initialContents)
  const [isLoading, setIsLoading] = useState(false)

  const latestRequestedKeyRef = useRef<DateRangeValue>(DEFAULT_DATE_RANGE)

  // Imagen vertical (fallback a la cuadrada): misma tarjeta póster que
  // `ProductosPagina`, donde la cuadrada se recortaba.
  const cards = useMemo(
    () => toProductCards(contents, { preferVerticalImage: true }),
    [contents],
  )
  // `view_item_list`/`select_item` (`Listado filtrado.csv`). El carrusel se
  // desmonta con el estado vacío y la lista cambia de ítems al cambiar de chip.
  const analytics = useMemo(
    () => ({
      itemListName: WEEKEND_LIST_NAME,
      moduleName: WEEKEND_MODULE_NAME,
      moduleType: WEEKEND_MODULE_TYPE,
      modulePosition,
      items: toModuleAnalyticsItems(cards, contents),
    }),
    [cards, contents, modulePosition],
  )

  function handleDateRangeChange(nextDateRange: DateRangeValue) {
    // Reelegir la chip ya vigente no dispara ninguna petición adicional.
    if (nextDateRange === latestRequestedKeyRef.current) return

    // `filter_change` (`Listado filtrado.csv` → «Cambio de filtro»), inmediato al
    // cambiar la chip. Siempre hay un periodo activo (nunca se "quita"), así que
    // `apply` con el periodo anterior como valor previo y `filters_active_count` =
    // periodo + provincia (si no es `todos`). ⚠️ PROVISIONAL: criterio no
    // documentado para un selector de periodo sin estado "sin filtro".
    pageAnalytics?.trackFilterChange({
      filter_action: 'apply',
      filter_type: 'fecha',
      filter_value: nextDateRange,
      filter_previous_value: latestRequestedKeyRef.current || NOT_SET,
      filters_active_count: String(1 + (province !== ALL_LOCATIONS ? 1 : 0)),
      module_name: WEEKEND_MODULE_NAME,
      module_type: WEEKEND_MODULE_TYPE,
    })

    latestRequestedKeyRef.current = nextDateRange
    setIsLoading(true)

    fetchFinDeSemanaAction(pageSlug, moduleId, province, nextDateRange)
      .then((freshContents) => {
        // Respuesta obsoleta: el visitante ya eligió otra chip mientras esta
        // petición estaba en curso — se descarta sin tocar el estado.
        if (latestRequestedKeyRef.current !== nextDateRange) return
        if (freshContents === null) {
          // El módulo ya no aparece en la respuesta — se trata como fallo,
          // conserva el último estado bueno (FR-009c).
          setIsLoading(false)
          return
        }
        setContents(freshContents)
        setDateRange(nextDateRange)
        setIsLoading(false)
      })
      .catch((error: unknown) => {
        if (latestRequestedKeyRef.current !== nextDateRange) return
        console.error('Error al actualizar el módulo Fin de semana', error)
        setIsLoading(false)
      })
  }

  return (
    <>
      {isLoading && <LoadingOverlay label={DATE_RANGE_CHANGE_LOADING_LABEL} />}

      <div className={styles.dataColumn}>
        <FinDeSemanaHeader
          headingId={headingId}
          dateRange={dateRange}
          provinceNicename={province}
          onDateRangeChange={handleDateRangeChange}
        />
      </div>

      <div className={styles.carouselColumn}>
        {cards.length === 0 ? (
          <p className={styles.emptyState}>{EMPTY_STATE_TITLE}</p>
        ) : (
          <ContextualProductCarousel key={dateRange} items={cards} analytics={analytics} />
        )}
      </div>
    </>
  )
}
